import { useEffect, useState } from "react";
import ApiDataBase from "server";

// The API sends all ten BeSTT keys on read and treats a missing key as false
// on write, so the selection only keeps the factors set to true.
export const normalizePenibilite = (penibilite) => {
    if (!penibilite || typeof penibilite !== "object") return {};
    return Object.keys(penibilite).reduce((selection, besttField) => {
        if (penibilite[besttField] === true) selection[besttField] = true;
        return selection;
    }, {});
};

export const hasSelectedPenibilite = (penibilite) =>
    Object.keys(normalizePenibilite(penibilite)).length > 0;

// The wizard keeps the selection while the switch is off so it can be turned
// back on without losing it; the API must only receive it when the flag is on.
export const buildTemplateConditions = (templateData) => {
    const positionAtRisk = !!templateData?.position_at_risk;
    return {
        position_at_risk: positionAtRisk,
        penibilite: positionAtRisk
            ? normalizePenibilite(templateData?.penibilite)
            : {},
    };
};

const toFallbackPenibiliteLabel = (besttField) =>
    besttField.replace(/^Penibilite_/, "").replace(/_/g, " ");

// Labels in referential order. While the referential is loading, or when it
// failed, a factor falls back to a label derived from its BeSTT key.
const getPenibiliteLabels = (penibilite, riskyPositions = []) => {
    const selection = normalizePenibilite(penibilite);
    const listed = riskyPositions.filter(
        ({ besttField }) => selection[besttField]
    );
    const listedFields = new Set(listed.map(({ besttField }) => besttField));
    return [
        ...listed.map(({ name }) => name),
        ...Object.keys(selection)
            .filter((besttField) => !listedFields.has(besttField))
            .map(toFallbackPenibiliteLabel),
    ];
};

export const formatRiskyPositionSummary = (
    conditions,
    language,
    riskyPositions
) => {
    if (!conditions?.position_at_risk) {
        return language.getValue(
            "missionTemplate.riskyPosition.summaryNo",
            "Non"
        );
    }
    const yes = language.getValue(
        "missionTemplate.riskyPosition.summaryYes",
        "Oui"
    );
    const labels = getPenibiliteLabels(conditions.penibilite, riskyPositions);
    return labels.length > 0 ? `${yes} — ${labels.join(", ")}` : yes;
};

const toSortedReferential = (response) =>
    (Array.isArray(response) ? response : [])
        .filter(
            (riskyPosition) =>
                typeof riskyPosition?.besttField === "string" &&
                riskyPosition.besttField !== ""
        )
        .sort(
            (a, b) =>
                (a.order ?? Number.MAX_SAFE_INTEGER) -
                (b.order ?? Number.MAX_SAFE_INTEGER)
        );

// One request per page session, shared by the wizard step, the confirmation
// and the preview panel. A failed request is dropped so the next mount
// retries instead of keeping the error.
let riskyPositionsRequest = null;
let loadedRiskyPositions = null;

const loadRiskyPositions = () => {
    if (!riskyPositionsRequest) {
        const request = new Promise((resolve, reject) =>
            ApiDataBase.getListRiskyPositions(resolve, reject)
        ).then((response) => {
            loadedRiskyPositions = toSortedReferential(response);
            return loadedRiskyPositions;
        });
        request.catch((err) => {
            console.warn(err);
            if (riskyPositionsRequest === request) riskyPositionsRequest = null;
        });
        riskyPositionsRequest = request;
    }
    return riskyPositionsRequest;
};

export const useRiskyPositions = () => {
    // Seeded from the cache so a component mounted after the first load
    // renders the list right away, without flashing the skeleton or the
    // fallback labels.
    const [riskyPositions, setRiskyPositions] = useState(loadedRiskyPositions);
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        let isMounted = true;
        loadRiskyPositions().then(
            (referential) => {
                if (isMounted) setRiskyPositions(referential);
            },
            () => {
                if (isMounted) setHasError(true);
            }
        );
        return () => {
            isMounted = false;
        };
    }, []);

    return {
        riskyPositions: riskyPositions || [],
        isLoading: !riskyPositions && !hasError,
        hasError,
    };
};
